'use client'

import { useState } from 'react'
import Link from 'next/link'

type AnyDoc = Record<string, any>

export interface EventsSectionStrings {
  sectionLabel: string
  sectionTitle: string
  viewAll: string
  listBtn: string
  calendarBtn: string
  featuredBadge: string
  viewDetails: string
  upcoming: string
  noEvents: string
  monthNames: string[]
  dayNames: string[]
  monthShort: string[]
}

function mediaUrl(val: any): string | undefined {
  if (!val) return undefined
  if (typeof val === 'string') return val
  if (val.url) return val.url
  if (val.filename) return `/api/media/file/${encodeURIComponent(val.filename)}`
  return undefined
}

function docImg(d: AnyDoc): string | undefined {
  return mediaUrl(d.coverImage ?? d.portraitImage)
}

// ── Large featured event card ─────────────────────────────────────────────
function EventFeaturedCard({ event, strings }: { event: AnyDoc; strings: EventsSectionStrings }) {
  const d   = event.startDate ? new Date(event.startDate) : null
  const img = docImg(event)
  return (
    <Link href={`/events/${event.slug}`} className={`hp-ev-feat${!img ? ' grad-event' : ''}`}>
      {img && (
        <div className="hp-ev-feat-img">
          <img src={img} alt={event.title ?? ''} />
        </div>
      )}
      <div className="hp-ev-feat-overlay" />
      {d && (
        <div className="hp-ev-feat-datebox">
          <div className="hp-ev-feat-mon">{strings.monthShort[d.getUTCMonth()]}</div>
          <div className="hp-ev-feat-day">{d.getUTCDate()}</div>
          <div className="hp-ev-feat-year">{d.getUTCFullYear()}</div>
        </div>
      )}
      <div className="hp-ev-feat-body">
        <div className="hp-ev-feat-badge">
          <span className="hp-ev-feat-dot" />
          {strings.featuredBadge}
        </div>
        <div className="hp-ev-feat-title">{event.title ?? event.name}</div>
        {event.locationName && (
          <div className="hp-ev-feat-loc">◎ {event.locationName}</div>
        )}
        <div className="hp-ev-feat-cta">{strings.viewDetails}</div>
      </div>
    </Link>
  )
}

// ── Small upcoming event card ─────────────────────────────────────────────
function EventSmallCard({ event, strings }: { event: AnyDoc; strings: EventsSectionStrings }) {
  const d   = event.startDate ? new Date(event.startDate) : null
  const img = docImg(event)
  return (
    <Link href={`/events/${event.slug}`} className="hp-ev-small">
      {img ? (
        <div className="hp-ev-small-img">
          <img src={img} alt={event.title ?? ''} />
        </div>
      ) : (
        <div className="hp-ev-small-img hp-ev-small-img--nophoto">
          {d && <span className="hp-ev-small-img-day">{d.getUTCDate()}</span>}
        </div>
      )}
      <div className="hp-ev-small-body">
        {d && (
          <div className="hp-ev-small-date">
            <span className="hp-ev-small-mon">{strings.monthShort[d.getUTCMonth()]}</span>
            <span className="hp-ev-small-daynum">{d.getUTCDate()}</span>
            <span className="hp-ev-small-year">{d.getUTCFullYear()}</span>
          </div>
        )}
        <div className="hp-ev-small-title">{event.title ?? event.name}</div>
        {event.locationName && (
          <div className="hp-ev-small-loc">◎ {event.locationName}</div>
        )}
      </div>
      <div className="hp-ev-small-arrow">→</div>
    </Link>
  )
}

// ── Mini calendar ─────────────────────────────────────────────────────────
function EventCalendar({
  events, calMonth, onMonthChange, strings,
}: {
  events: AnyDoc[]
  calMonth: { year: number; month: number }
  onMonthChange: (m: { year: number; month: number }) => void
  strings: EventsSectionStrings
}) {
  const { year, month } = calMonth

  const firstDay  = new Date(year, month, 1)
  const lastDay   = new Date(year, month + 1, 0)
  let startDow = firstDay.getDay()
  startDow = startDow === 0 ? 6 : startDow - 1

  const totalCells = Math.ceil((startDow + lastDay.getDate()) / 7) * 7
  const cells: (number | null)[] = Array(totalCells).fill(null)
  for (let d = 1; d <= lastDay.getDate(); d++) cells[startDow + d - 1] = d

  const eventsByDay: Record<number, AnyDoc[]> = {}
  for (const ev of events) {
    if (!ev.startDate) continue
    const dt = new Date(ev.startDate)
    if (dt.getUTCFullYear() === year && dt.getUTCMonth() === month) {
      const day = dt.getUTCDate()
      if (!eventsByDay[day]) eventsByDay[day] = []
      eventsByDay[day].push(ev)
    }
  }

  const now = new Date()
  const isToday = (d: number) =>
    d === now.getDate() && month === now.getMonth() && year === now.getFullYear()

  const prevMonth = () => month === 0
    ? onMonthChange({ year: year - 1, month: 11 })
    : onMonthChange({ year, month: month - 1 })
  const nextMonth = () => month === 11
    ? onMonthChange({ year: year + 1, month: 0 })
    : onMonthChange({ year, month: month + 1 })

  const hasEventsThisMonth = Object.keys(eventsByDay).length > 0

  return (
    <div className="hp-cal">
      <div className="hp-cal-nav">
        <button className="hp-cal-nav-btn" onClick={prevMonth} aria-label="Previous month">←</button>
        <div className="hp-cal-month-label">
          <span className="hp-cal-month-name">{strings.monthNames[month]}</span>
          <span className="hp-cal-month-year">{year}</span>
        </div>
        <button className="hp-cal-nav-btn" onClick={nextMonth} aria-label="Next month">→</button>
      </div>

      <div className="hp-cal-dow-row">
        {strings.dayNames.map(d => <div key={d} className="hp-cal-dow">{d}</div>)}
      </div>

      <div className="hp-cal-grid">
        {cells.map((day, i) => {
          const evs = day ? (eventsByDay[day] ?? []) : []
          return (
            <div
              key={i}
              className={[
                'hp-cal-cell',
                !day        ? 'hp-cal-cell--empty'      : '',
                day && isToday(day) ? 'hp-cal-cell--today' : '',
                evs.length  ? 'hp-cal-cell--has-events' : '',
              ].filter(Boolean).join(' ')}
            >
              {day && (
                <>
                  <span className="hp-cal-day-num">{day}</span>
                  <div className="hp-cal-ev-list">
                    {evs.map(ev => (
                      <Link key={ev.id} href={`/events/${ev.slug}`} className="hp-cal-ev-chip">
                        {ev.title ?? ev.name}
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>
          )
        })}
      </div>

      {!hasEventsThisMonth && (
        <div className="hp-cal-empty-msg">{strings.noEvents} {strings.monthNames[month]}.</div>
      )}
    </div>
  )
}

// ── Main exported component ───────────────────────────────────────────────
export function EventsSection({ events, strings }: { events: AnyDoc[]; strings: EventsSectionStrings }) {
  const [view, setView] = useState<'list' | 'calendar'>('list')

  const initialMonth = (() => {
    const first = events.find(e => e.startDate)
    const d = first ? new Date(first.startDate) : new Date()
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() }
  })()
  const [calMonth, setCalMonth] = useState(initialMonth)

  if (!events.length) return null

  const featuredEvent  = events[0]
  const upcomingEvents = events.slice(1, 4)

  return (
    <section className="hp-section hp-section--events">
      <div className="hp-section-inner">
        <div className="hp-section-head">
          <div>
            <div className="hp-section-label hp-section-label--event">{strings.sectionLabel}</div>
            <h2 className="hp-section-title hp-section-title--light">{strings.sectionTitle}</h2>
          </div>
          <div className="hp-ev-controls">
            <div className="hp-ev-view-toggle">
              <button
                className={`hp-ev-toggle-btn${view === 'list' ? ' hp-ev-toggle-btn--active' : ''}`}
                onClick={() => setView('list')}
                aria-pressed={view === 'list'}
              >
                <span className="hp-ev-toggle-icon">≡</span> {strings.listBtn}
              </button>
              <button
                className={`hp-ev-toggle-btn${view === 'calendar' ? ' hp-ev-toggle-btn--active' : ''}`}
                onClick={() => setView('calendar')}
                aria-pressed={view === 'calendar'}
              >
                <span className="hp-ev-toggle-icon">▦</span> {strings.calendarBtn}
              </button>
            </div>
            <Link href="/events" className="hp-view-all hp-view-all--light">{strings.viewAll}</Link>
          </div>
        </div>

        {view === 'list' ? (
          <div className="hp-events-featured-grid">
            <EventFeaturedCard event={featuredEvent} strings={strings} />
            {upcomingEvents.length > 0 && (
              <div className="hp-ev-upcoming-col">
                <div className="hp-ev-upcoming-label">
                  <span className="hp-ev-upcoming-dot" />
                  {strings.upcoming}
                </div>
                {upcomingEvents.map(e => <EventSmallCard key={e.id} event={e} strings={strings} />)}
              </div>
            )}
          </div>
        ) : (
          <EventCalendar events={events} calMonth={calMonth} onMonthChange={setCalMonth} strings={strings} />
        )}
      </div>
    </section>
  )
}
